'use client'

import { useRef } from 'react'

import dynamic from 'next/dynamic'

import { GlossaryPanel } from '@/components/client/glossary/glossary-panel'
import { trackCustomEvent } from '@/hooks/client/use-analytics-engine'
import { useCompared } from '@/hooks/client/use-compared'
import { useTranslation } from '@/hooks/client/use-translation'
import { EventType } from '@/lib/analytics/events'
import type { FilterOptions } from '@/lib/directory/build-filter-options'
import type { DeferredDirectoryCatalog } from '@/lib/directory/deferred-catalog'
import type { PackedDirectory } from '@/lib/directory/pack-releases'
import { DIRECTORY_PAGE_SIZE } from '@/lib/directory/present-releases'
import { glossaryLabels } from '@/lib/glossary/labels'

import { CatalogError } from './catalog-error'
import { FilterRail } from './filters/filter-rail'
import { ReleaseList } from './release-list'
import { ResultsPagination } from './results-pagination'
import { ResultsToolbar } from './results-toolbar'
import { trackDirectory } from './track-directory'
import { useDirectoryCatalog } from './use-directory-catalog'
import { useDirectoryPresentation } from './use-directory-presentation'
import { ActionType, useDirectoryReducer } from './use-directory-reducer'
import { useDirectoryUrl } from './use-directory-url'

const CompareDock = dynamic(() => import('./compare-dock').then((module) => module.CompareDock))
const FilterSheet = dynamic(() => import('./filters/filter-sheet').then((module) => module.FilterSheet))

interface DirectoryPageProps {
  /** Every comparable family, packed for the wire (see pack-releases.ts). */
  packed: PackedDirectory
  filterOptions: FilterOptions
  /** Families per page to start with (ten, as on the directory). */
  pageSize?: number
  /** Sizes the reader may switch between; none means no page-size control. */
  pageSizeOptions?: number[]
  /** Embedded directories can keep offscreen thumbnails out of the initial load. */
  eagerImageCount?: number
  /** Keep a complete first page in HTML and load the rest near the workspace. */
  deferredCatalog?: DeferredDirectoryCatalog
}

// The directory workspace: a filter rail (desktop) or sheet (phones), the
// results toolbar and rows, and the compare tray. Filter/sort/search state is
// local (the reducer); the compare selection is the shared useCompared store.
export const DirectoryPage = ({
  packed,
  filterOptions,
  pageSize = DIRECTORY_PAGE_SIZE,
  pageSizeOptions = [],
  eagerImageCount = 3,
  deferredCatalog,
}: DirectoryPageProps) => {
  const { t } = useTranslation()
  const [state, dispatch] = useDirectoryReducer(pageSize)
  const { compared, toggle, atMax } = useCompared()
  const resultsRef = useRef<HTMLDivElement>(null)
  const catalogRef = useRef<HTMLDivElement>(null)
  const catalog = useDirectoryCatalog(catalogRef, packed, deferredCatalog, state, pageSize, compared.size)
  const { releases, ready } = catalog
  useDirectoryUrl(state, dispatch)
  const { paged, counts, facetLabels, modelCount, totalModelCount, totalCount } = useDirectoryPresentation(
    releases,
    filterOptions,
    state,
    ready,
    deferredCatalog
  )

  const handleToggleCompare = (slug: string) => {
    // Match the old site's compare-add-product--click event (fired only when a
    // product is added, not removed). trackCustomEvent passes the event name
    // through verbatim, so include the suffix the old `track()` helper added.
    if (!compared.has(slug)) {
      trackCustomEvent('compare-add-product--click', EventType.Click, { eventRelease: slug, origin: 'directory' })
    }
    toggle(slug)
  }

  // A page change lands the reader at the top of the new page's rows, with
  // focus on the results column so the next Tab reaches the first row.
  const handlePageChange = (page: number) => {
    trackDirectory('directory-page', { page })
    dispatch({ type: ActionType.SetPage, page })
    resultsRef.current?.scrollIntoView({ block: 'start' })
    resultsRef.current?.focus({ preventScroll: true })
  }

  const handlePageSizeChange = (size: number) => {
    trackDirectory('directory-page-size', { pageSize: size })
    dispatch({ type: ActionType.SetPageSize, pageSize: size })
  }
  // The per-page choice only shows when the smallest size would still page.
  const sizeOptions = pageSizeOptions.length > 0 && paged.total > Math.min(...pageSizeOptions) ? pageSizeOptions : []

  return (
    // Bottom clearance follows the tray's measured height (a CSS variable it
    // publishes on <html>), so nothing hides behind it at any pick count.
    <div
      ref={catalogRef}
      data-glossary-scope
      aria-busy={catalog.loading || undefined}
      className="mt-8 pb-[calc(var(--compare-dock-h,0px)+1.5rem)] md:mt-10"
    >
      <div className="lg:grid lg:grid-cols-[232px_minmax(0,1fr)] lg:gap-x-8 laptop:grid-cols-[264px_minmax(0,1fr)] laptop:gap-x-12">
        <FilterRail filters={state.filters} filterOptions={filterOptions} counts={counts} dispatch={dispatch} />
        <div ref={resultsRef} tabIndex={-1} className="min-w-0 scroll-mt-24 outline-none">
          <ResultsToolbar
            state={state}
            dispatch={dispatch}
            resultCount={paged.total}
            totalCount={totalCount}
            modelCount={modelCount}
            totalModelCount={totalModelCount}
            facetLabels={facetLabels}
            loading={catalog.loading}
            resultsReady={ready}
          />
          {catalog.status === 'error' ? <CatalogError onRetry={catalog.retry} /> : null}
          <ReleaseList
            releases={paged.items}
            eagerImageCount={eagerImageCount}
            compared={compared}
            atMax={atMax}
            facetLabels={facetLabels}
            onToggleCompare={handleToggleCompare}
            onClearFilters={() => dispatch({ type: ActionType.ClearFilters })}
          />
          {paged.pageCount > 1 || sizeOptions.length > 0 ? (
            <ResultsPagination
              paged={paged}
              onPageChange={handlePageChange}
              pageSizeChoice={
                sizeOptions.length > 0
                  ? { pageSize: state.pageSize, options: sizeOptions, onChange: handlePageSizeChange }
                  : undefined
              }
            />
          ) : null}
        </div>
      </div>
      {state.isFilterModalOpen ? (
        <FilterSheet
          isOpen={state.isFilterModalOpen}
          filters={state.filters}
          sort={state.sort}
          filterOptions={filterOptions}
          counts={counts}
          dispatch={dispatch}
          resultCount={paged.total}
        />
      ) : null}
      {compared.size > 0 ? (
        <CompareDock compared={compared} releases={releases} filters={state.filters} onRemove={toggle} />
      ) : null}
      <GlossaryPanel
        labels={{
          ...glossaryLabels(t),
          note: t(
            'directory.classificationHint',
            'Abbreviations describe the hearing aid’s style and buying category.'
          ),
        }}
      />
    </div>
  )
}
